import {
    Directive,
    Input,
    Output,
    EventEmitter,
    ElementRef,
    HostListener,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    ViewContainerRef,
    ComponentRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TellerOnlineWysiwygEditorComponent } from '../components/wysiwyg-editor/wysiwyg-editor.component';

export interface InlineEditDropdownOption {
    value: string | null;
    label: string;
}

export interface InlineEditConfig {
    type: 'text' | 'textarea' | 'rich-text' | 'wysiwyg' | 'dropdown';
    placeholder?: string;
    maxLength?: number;
    allowedTags?: string[];
    options?: InlineEditDropdownOption[];
    allowLists?: boolean;
}

export interface InlineEditChangeEvent {
    value: string;
    field: string;
}

@Directive({
    selector: '[appInlineEdit]',
    standalone: false,
    host: {
        '[attr.data-inline-edit]': 'appInlineEdit'
    }
})
export class InlineEditDirective implements OnInit, OnChanges, OnDestroy {
    private static _activeInstance: InlineEditDirective | null = null;

    /**
     * Returns true if any inline editor is currently open.
     */
    public static get isAnyActive(): boolean {
        return InlineEditDirective._activeInstance !== null;
    }

    @Input() appInlineEdit: string = ''; // Field identifier

    @Input() inlineEditValue: string = '';

    @Input() inlineEditConfig: InlineEditConfig = { type: 'text' };

    @Input() inlineEditEnabled: boolean = false;

    @Output() inlineEditChange = new EventEmitter<InlineEditChangeEvent>();

    private isEditing = false;

    private originalElement: HTMLElement;

    private editElement: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null = null;

    private editWrapper: HTMLDivElement | null = null;

    private subscription = new Subscription();

    private focusableElements: HTMLElement[] = [];

    private previouslyFocusedElement: HTMLElement | null = null;

    private wysiwygComponentRef: ComponentRef<TellerOnlineWysiwygEditorComponent> | null = null;

    private wysiwygContent: string = '';

    private scrollHandler: (() => void) | null = null;

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private viewContainer: ViewContainerRef
    ) {
        this.originalElement = this.elementRef.nativeElement;
    }

    ngOnInit() {
        this.updateAccessibilityAttributes();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.inlineEditEnabled) {
            this.updateAccessibilityAttributes();
        }
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        if (this.isEditing) {
            this.cancelEditing();
        }
        // Defensive cleanup in case isEditing was false but resources weren't cleaned
        if (this.scrollHandler) {
            document.removeEventListener('scroll', this.scrollHandler, true);
            this.scrollHandler = null;
        }
        if (this.wysiwygComponentRef) {
            this.wysiwygComponentRef.destroy();
            this.wysiwygComponentRef = null;
        }
        if (this.editWrapper) {
            this.editWrapper.remove();
            this.editWrapper = null;
        }
        if (InlineEditDirective._activeInstance === this) {
            InlineEditDirective._activeInstance = null;
        }
    }

    private updateAccessibilityAttributes() {
        if (this.inlineEditEnabled) {
            this.originalElement.setAttribute('aria-label',
                `Editable ${this.appInlineEdit || 'content'}. Press Enter or Space to edit.`);
            this.originalElement.setAttribute('role', 'button');
            this.originalElement.classList.add('inline-editable');
        } else {
            this.originalElement.removeAttribute('aria-label');
            this.originalElement.removeAttribute('role');
            this.originalElement.classList.remove('inline-editable');
        }
    }

    private handleTabNavigation(event: KeyboardEvent) {
        if (this.focusableElements.length === 0) return;

        const currentIndex = this.focusableElements.findIndex(el => el === document.activeElement);

        if (event.shiftKey) {
            // Shift+Tab: go to previous element
            event.preventDefault();
            const previousIndex = currentIndex <= 0 ? this.focusableElements.length - 1 : currentIndex - 1;
            this.focusableElements[previousIndex].focus();
        } else {
            // Tab: go to next element
            event.preventDefault();
            const nextIndex = currentIndex >= this.focusableElements.length - 1 ? 0 : currentIndex + 1;
            this.focusableElements[nextIndex].focus();
        }
    }

    @HostListener('click', ['$event'])
    onClick(event: Event) {
        const target = event.target as HTMLElement;

        // Don't interfere with clicks inside the edit wrapper
        if (target?.closest('.inline-edit-wrapper')) {
            return;
        }

        // Don't start editing if already editing
        if (this.isEditing) {
            return;
        }

        if (this.inlineEditEnabled) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            this.startEditing();
        }
    }

    @HostListener('keydown.enter', ['$event'])
    onEnter(event: KeyboardEvent) {
        const target = event.target as HTMLElement;
        if (this.isEditing && target?.closest('.inline-edit-wrapper')) {
            return;
        }

        if (this.inlineEditEnabled && !this.isEditing) {
            event.preventDefault();
            event.stopPropagation();
            this.startEditing();
        }
    }

    @HostListener('keydown.space', ['$event'])
    onSpace(event: KeyboardEvent) {
        const target = event.target as HTMLElement;
        if (this.isEditing && target?.closest('.inline-edit-wrapper')) {
            return;
        }

        if (this.inlineEditEnabled && !this.isEditing) {
            event.preventDefault();
            event.stopPropagation();
            this.startEditing();
        }
    }

    @HostListener('document:keydown.escape')
    onEscape() {
        if (this.isEditing) {
            this.cancelEditing();
        }
    }

    private startEditing() {
        if (InlineEditDirective._activeInstance !== null) {
            return;
        }

        InlineEditDirective._activeInstance = this;
        this.previouslyFocusedElement = document.activeElement as HTMLElement;
        this.isEditing = true;

        if (this.inlineEditConfig.type === 'wysiwyg') {
            this.createWysiwygEditor();
        } else if (this.inlineEditConfig.type === 'dropdown') {
            this.createDropdownElement();
        } else {
            this.createEditElement();
        }

        this.addEditingClass();
    }

    private createWysiwygEditor() {
        const rect = this.originalElement.getBoundingClientRect();
        const width = Math.max(300, rect.width);
        const viewportHeight = window.innerHeight;

        // Calculate available space below and above the element
        const spaceBelow = viewportHeight - rect.top - 20; // 20px margin
        const spaceAbove = rect.bottom - 20;

        // Desired height for the editor (content area + buttons)
        const desiredHeight = 250;

        // Determine positioning strategy
        let top: number;
        let maxHeight: number;

        if (spaceBelow >= desiredHeight) {
            // Enough space below - position at element top, grow downward
            top = rect.top;
            maxHeight = spaceBelow;
        } else if (spaceAbove >= desiredHeight) {
            // Not enough space below, but enough above - position above element
            top = Math.max(20, rect.bottom - desiredHeight);
            maxHeight = spaceAbove;
        } else {
            // Limited space both ways - use whichever has more space
            if (spaceBelow >= spaceAbove) {
                top = rect.top;
                maxHeight = spaceBelow;
            } else {
                top = 20;
                maxHeight = rect.bottom - 20;
            }
        }

        // Create wrapper with fixed position (escapes overflow:hidden). Static appearance comes
        // from .gcr-inline-edit (styles/gcr/_inline-edit.scss); only runtime geometry is inline.
        const wrapper = document.createElement('div');
        wrapper.className = 'inline-edit-wrapper gcr-inline-edit';
        wrapper.style.cssText = `
            position: fixed;
            top: ${top}px;
            left: ${rect.left}px;
            width: ${width}px;
            max-height: ${maxHeight}px;
            overflow-y: auto;
        `;
        this.editWrapper = wrapper;

        // Add scroll handler to reposition the wrapper when page scrolls
        // Use document instead of window to catch scrolls on any scrollable container
        this.scrollHandler = () => {
            if (!this.editWrapper) return;
            // Recalculate position based on where the original element is NOW
            const currentRect = this.originalElement.getBoundingClientRect();
            const currentViewportHeight = window.innerHeight;
            const currentSpaceBelow = currentViewportHeight - currentRect.top - 20;
            const currentSpaceAbove = currentRect.bottom - 20;

            let newTop: number;
            let newMaxHeight: number;

            if (currentSpaceBelow >= desiredHeight) {
                newTop = currentRect.top;
                newMaxHeight = currentSpaceBelow;
            } else if (currentSpaceAbove >= desiredHeight) {
                newTop = Math.max(20, currentRect.bottom - desiredHeight);
                newMaxHeight = currentSpaceAbove;
            } else if (currentSpaceBelow >= currentSpaceAbove) {
                    newTop = currentRect.top;
                    newMaxHeight = currentSpaceBelow;
                } else {
                    newTop = 20;
                    newMaxHeight = currentRect.bottom - 20;
                }

            this.editWrapper.style.top = `${newTop}px`;
            this.editWrapper.style.left = `${currentRect.left}px`;
            this.editWrapper.style.maxHeight = `${newMaxHeight}px`;
        };
        // Listen on document with capture to catch all scroll events including nested scrollable containers
        document.addEventListener('scroll', this.scrollHandler, true);

        // Create buttons (appearance via .gcr-inline-edit__* in styles/gcr/_inline-edit.scss)
        const buttonsDiv = document.createElement('div');
        buttonsDiv.className = 'gcr-inline-edit__buttons';

        const saveBtn = document.createElement('button');
        saveBtn.textContent = 'Save';
        saveBtn.className = 'gcr-inline-edit__button gcr-inline-edit__save';

        const cancelBtn = document.createElement('button');
        cancelBtn.textContent = 'Cancel';
        cancelBtn.className = 'gcr-inline-edit__button gcr-inline-edit__cancel';

        buttonsDiv.appendChild(saveBtn);
        buttonsDiv.appendChild(cancelBtn);

        // Create editor container
        const editorContainer = document.createElement('div');
        editorContainer.className = 'wysiwyg-editor-container';

        // Create the wysiwyg component
        this.wysiwygComponentRef = this.viewContainer.createComponent(TellerOnlineWysiwygEditorComponent);
        this.wysiwygComponentRef.instance.initialContent = (this.inlineEditValue !== null && this.inlineEditValue !== undefined)
            ? this.inlineEditValue
            : (this.originalElement.innerHTML || '');
        this.wysiwygComponentRef.instance.placeholder = this.inlineEditConfig.placeholder || 'Enter content...';
        if (this.inlineEditConfig.allowLists !== undefined) {
            this.wysiwygComponentRef.instance.allowLists = this.inlineEditConfig.allowLists;
        }

        // Subscribe to content changes
        this.subscription.add(
            this.wysiwygComponentRef.instance.contentChange.subscribe((content: string) => {
                this.wysiwygContent = content;
            })
        );

        // Move the component's DOM element into our container
        editorContainer.appendChild(this.wysiwygComponentRef.location.nativeElement);
        this.wysiwygContent = this.wysiwygComponentRef.instance.initialContent;

        // Assemble the wrapper
        wrapper.appendChild(editorContainer);
        wrapper.appendChild(buttonsDiv);

        // Append to body
        document.body.appendChild(wrapper);

        // Event listeners
        saveBtn.addEventListener('click', () => this.saveEditing());
        cancelBtn.addEventListener('click', () => this.cancelEditing());

        saveBtn.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Tab') {
                this.handleTabNavigation(e);
            }
        });

        cancelBtn.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Tab') {
                this.handleTabNavigation(e);
            }
        });

        this.focusableElements = [saveBtn, cancelBtn];
    }

    private createEditElement() {
        // Capture the original text content BEFORE appending any wrapper elements,
        // otherwise textContent will include button text like "SaveCancel"
        const originalTextContent = this.originalElement.textContent || '';

        // Set the original element as positioning context
        const originalPosition = window.getComputedStyle(this.originalElement).position;
        if (originalPosition === 'static') {
            this.originalElement.style.position = 'relative';
        }

        // Get original element dimensions for min-height
        const rect = this.originalElement.getBoundingClientRect();
        const minHeight = Math.max(100, rect.height);

        // Create wrapper div positioned within the original element. Static appearance comes
        // from .gcr-inline-edit (styles/gcr/_inline-edit.scss); only runtime geometry is inline.
        const wrapper = document.createElement('div');
        wrapper.className = 'inline-edit-wrapper gcr-inline-edit';
        this.editWrapper = wrapper;

        // Mirrors the .gcr-inline-edit padding (space(2)); used in the textarea height math below.
        const wrapperPadding = 8;
        wrapper.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            min-width: 300px;
            min-height: ${minHeight}px;
        `;

        // Create buttons first so we can measure their actual height (appearance via
        // .gcr-inline-edit__* in styles/gcr/_inline-edit.scss)
        const buttonsDiv = document.createElement('div');
        // Mirrors the .gcr-inline-edit__buttons margin-top (space(2)); used in the height math below.
        const buttonDivMargin = 8;
        buttonsDiv.className = 'gcr-inline-edit__buttons';

        const saveBtn = document.createElement('button');
        saveBtn.textContent = 'Save';
        saveBtn.className = 'gcr-inline-edit__button gcr-inline-edit__save';

        const cancelBtn = document.createElement('button');
        cancelBtn.textContent = 'Cancel';
        cancelBtn.className = 'gcr-inline-edit__button gcr-inline-edit__cancel';

        // Add buttons to wrapper and append to original element
        buttonsDiv.appendChild(saveBtn);
        buttonsDiv.appendChild(cancelBtn);
        wrapper.appendChild(buttonsDiv);
        this.originalElement.appendChild(wrapper);

        // Measure actual button height
        const actualButtonHeight = buttonsDiv.getBoundingClientRect().height;

        // Create input based on type
        let inputElement: HTMLInputElement | HTMLTextAreaElement;

        if (this.inlineEditConfig.type === 'textarea' || this.inlineEditConfig.type === 'rich-text') {
            inputElement = document.createElement('textarea');

            // Calculate dynamic height based on original element
            const originalHeight = this.originalElement.getBoundingClientRect().height;
            const minTextareaHeight = 60;
            // Mirrors the .gcr-inline-edit__input padding (space(1)); used in the height math below.
            const textareaPadding = 4;

            const calculatedHeight = Math.max(minTextareaHeight, originalHeight - (wrapperPadding * 2) - actualButtonHeight - buttonDivMargin - (textareaPadding * 2));

            inputElement.rows = Math.max(3, Math.ceil(calculatedHeight / 20));
            // Appearance via .gcr-inline-edit__input(.--textarea); only computed height is inline.
            inputElement.className = 'gcr-inline-edit__input gcr-inline-edit__input--textarea';
            inputElement.style.cssText = `
                height: ${calculatedHeight}px;
                min-height: ${minTextareaHeight}px;
            `;

            this.editElement = inputElement;
            inputElement.value = (this.inlineEditValue !== null && this.inlineEditValue !== undefined)
                ? this.inlineEditValue
                : originalTextContent;

            if (this.inlineEditConfig.placeholder) {
                inputElement.placeholder = this.inlineEditConfig.placeholder;
            }

            wrapper.insertBefore(this.editElement, buttonsDiv);
            this.setupStandardEventListeners(saveBtn, cancelBtn);
        } else {
            inputElement = document.createElement('input');
            inputElement.setAttribute('type', 'text');
            const { textAlign } = window.getComputedStyle(this.originalElement);
            // Appearance via .gcr-inline-edit__input; only the inherited text-align is inline.
            inputElement.className = 'gcr-inline-edit__input';
            inputElement.style.cssText = `
                text-align: ${textAlign};
            `;

            this.editElement = inputElement;
            inputElement.value = (this.inlineEditValue !== null && this.inlineEditValue !== undefined)
                ? this.inlineEditValue
                : originalTextContent;

            if (this.inlineEditConfig.placeholder) {
                inputElement.placeholder = this.inlineEditConfig.placeholder;
            }

            wrapper.insertBefore(this.editElement, buttonsDiv);
            this.setupStandardEventListeners(saveBtn, cancelBtn);
        }
    }

    private createDropdownElement() {
        // Set the original element as positioning context
        const originalPosition = window.getComputedStyle(this.originalElement).position;
        if (originalPosition === 'static') {
            this.originalElement.style.position = 'relative';
        }

        // Create wrapper div positioned within the original element. Static appearance comes
        // from .gcr-inline-edit (styles/gcr/_inline-edit.scss); only runtime geometry is inline.
        const wrapper = document.createElement('div');
        wrapper.className = 'inline-edit-wrapper gcr-inline-edit';
        this.editWrapper = wrapper;

        wrapper.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            min-width: 300px;
        `;

        // Create buttons (appearance via .gcr-inline-edit__* in styles/gcr/_inline-edit.scss)
        const buttonsDiv = document.createElement('div');
        buttonsDiv.className = 'gcr-inline-edit__buttons';

        const saveBtn = document.createElement('button');
        saveBtn.textContent = 'Save';
        saveBtn.className = 'gcr-inline-edit__button gcr-inline-edit__save';

        const cancelBtn = document.createElement('button');
        cancelBtn.textContent = 'Cancel';
        cancelBtn.className = 'gcr-inline-edit__button gcr-inline-edit__cancel';

        buttonsDiv.appendChild(saveBtn);
        buttonsDiv.appendChild(cancelBtn);

        // Create select element (appearance via .gcr-inline-edit__input(.--select))
        const selectElement = document.createElement('select');
        selectElement.className = 'gcr-inline-edit__input gcr-inline-edit__input--select';

        // Add placeholder option if configured
        if (this.inlineEditConfig.placeholder) {
            const placeholderOption = document.createElement('option');
            placeholderOption.value = '';
            placeholderOption.textContent = this.inlineEditConfig.placeholder;
            placeholderOption.disabled = true;
            selectElement.appendChild(placeholderOption);
        }

        // Add options from config
        const options = this.inlineEditConfig.options || [];
        options.forEach(opt => {
            const optionElement = document.createElement('option');
            // Convert value to string for the option element
            optionElement.value = opt.value != null ? String(opt.value) : '';
            optionElement.textContent = opt.label;
            selectElement.appendChild(optionElement);
        });

        // Set current value - convert to string for comparison since option values are strings
        const currentValue = this.inlineEditValue != null ? String(this.inlineEditValue) : '';
        selectElement.value = currentValue;

        // If the current value doesn't match any option and there's a placeholder, select it
        if (selectElement.value !== currentValue && this.inlineEditConfig.placeholder) {
            selectElement.selectedIndex = 0;
        }

        this.editElement = selectElement;

        // Assemble wrapper
        wrapper.appendChild(selectElement);
        wrapper.appendChild(buttonsDiv);
        this.originalElement.appendChild(wrapper);

        // Setup event listeners
        this.setupDropdownEventListeners(saveBtn, cancelBtn, selectElement);
    }

    private setupDropdownEventListeners(saveBtn: HTMLButtonElement, cancelBtn: HTMLButtonElement, selectElement: HTMLSelectElement) {
        saveBtn.addEventListener('click', () => this.saveEditing());
        cancelBtn.addEventListener('click', () => this.cancelEditing());

        selectElement.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                this.saveEditing();
            } else if (e.key === 'Tab') {
                this.handleTabNavigation(e);
            }
        });

        saveBtn.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Tab') {
                this.handleTabNavigation(e);
            }
        });

        cancelBtn.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Tab') {
                this.handleTabNavigation(e);
            }
        });

        this.focusableElements = [selectElement, saveBtn, cancelBtn];
        selectElement.focus();
    }

    private setupStandardEventListeners(saveBtn: HTMLButtonElement, cancelBtn: HTMLButtonElement) {
        saveBtn.addEventListener('click', () => this.saveEditing());
        cancelBtn.addEventListener('click', () => this.cancelEditing());

        if (this.editElement) {
            this.editElement.addEventListener('keydown', (e: KeyboardEvent) => {
                if (e.key === 'Enter' && !e.shiftKey && this.inlineEditConfig.type === 'text') {
                    e.preventDefault();
                    e.stopPropagation();
                    this.saveEditing();
                } else if (e.key === 'Tab') {
                    this.handleTabNavigation(e);
                }
            });
        }

        saveBtn.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Tab') {
                this.handleTabNavigation(e);
            }
        });

        cancelBtn.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Tab') {
                this.handleTabNavigation(e);
            }
        });

        if (this.editElement) {
            this.focusableElements = [this.editElement, saveBtn, cancelBtn];
            this.editElement.focus();
            if ('select' in this.editElement) {
                this.editElement.select();
            }
        }
    }

    private addEditingClass() {
        this.originalElement.classList.add('inline-editing');
    }

    private removeEditingClass() {
        this.originalElement.classList.remove('inline-editing');
    }

    private saveEditing() {
        let newValue: string;

        if (this.inlineEditConfig.type === 'wysiwyg' && this.wysiwygComponentRef) {
            newValue = this.wysiwygComponentRef.instance.getContent();
        } else if (this.editElement) {
            newValue = this.editElement.value;
        } else {
            return;
        }

        this.inlineEditChange.emit({
            value: newValue,
            field: this.appInlineEdit
        });
        this.finishEditing();
    }

    private cancelEditing() {
        this.finishEditing();
    }

    private finishEditing() {
        // Remove scroll handler if it exists
        if (this.scrollHandler) {
            document.removeEventListener('scroll', this.scrollHandler, true);
            this.scrollHandler = null;
        }

        // Destroy wysiwyg component if it exists
        if (this.wysiwygComponentRef) {
            this.wysiwygComponentRef.destroy();
            this.wysiwygComponentRef = null;
        }

        // Remove wrapper
        if (this.editWrapper) {
            this.editWrapper.remove();
        }
        this.editWrapper = null;

        // Reset original element position if we changed it
        if (this.originalElement.style.position === 'relative') {
            this.originalElement.style.position = '';
        }

        this.editElement = null;
        this.removeEditingClass();
        this.isEditing = false;
        if (InlineEditDirective._activeInstance === this) {
            InlineEditDirective._activeInstance = null;
        }
        this.focusableElements = [];
        this.wysiwygContent = '';

        // Restore focus to the element that was being edited
        this.originalElement.focus();
        this.previouslyFocusedElement = null;
    }
}
